import { useNavigate } from 'react-router-dom';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { outlookLoginEnabled, msLoginRequest } from '../authConfig';
import './Login.css';
import React, { useState, useEffect, useRef } from 'react';

interface LoginProps {
  onLogin: () => void;
}

// Separate allow-lists for each login provider. Set these in .env as
// comma-separated email lists, e.g.
//   REACT_APP_ALLOWED_OUTLOOK_EMAILS=you@outlook.com,teammate@company.com
//   REACT_APP_ALLOWED_GOOGLE_EMAILS=you@gmail.com,another@gmail.com
// Leave either one empty/unset to allow any email through on that provider
// (no restriction).
const ALLOWED_OUTLOOK_EMAILS = (process.env.REACT_APP_ALLOWED_OUTLOOK_EMAILS || '')
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);

const ALLOWED_GOOGLE_EMAILS = (process.env.REACT_APP_ALLOWED_GOOGLE_EMAILS || '')
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);

const isEmailAllowed = (email: string, allowedList: string[]): boolean => {
  if (allowedList.length === 0) {
    return true; // no restriction configured for this provider
  }
  return allowedList.includes(email.toLowerCase());
};

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isOutlookLoading, setIsOutlookLoading] = useState(false);

  const navigate = useNavigate();
  const { instance: msalInstance } = useMsal();

  const isOutlookLoginInFlightRef = useRef(false);

  // Fetches the signed-in user's photo from Microsoft Graph and converts it
  // to a data URL. Returns null (not an error) if the account simply has no
  // photo set — many personal Microsoft/Outlook.com accounts don't.
  const fetchOutlookProfilePicture = async (accessToken: string): Promise<string | null> => {
    try {
      const response = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        return null;
      }

      const blob = await response.blob();

      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.error('Failed to fetch Outlook profile picture:', err);
      return null;
    }
  };

  // Runs on every mount of this component — including the one that happens
  // when Microsoft redirects the whole page back here after Outlook login.
  // handleRedirectPromise() reads the auth response out of the URL hash and
  // resolves it; it resolves to null on a normal (non-redirect) page load.
  useEffect(() => {
    const processRedirectResult = async () => {
      try {
        const result = await msalInstance.handleRedirectPromise();

        if (result && result.account) {
          const account = result.account;

          const outlookEmail = (account.username || '').toLowerCase();
          if (!isEmailAllowed(outlookEmail, ALLOWED_OUTLOOK_EMAILS)) {
            setError('This Outlook account is not authorized to access this application.');
            return; // stop here — no session is created, user stays on the login page
          }

          sessionStorage.setItem('racs_auth_method', 'outlook');
          sessionStorage.setItem('racs_user_email', account.username ?? '');
          sessionStorage.setItem('racs_user_name', account.name ?? '');

          // Requesting the 'User.Read' scope at login also returns an
          // access token valid for Microsoft Graph, which is what lets us
          // fetch the profile photo here without a second sign-in.
          if (result.accessToken) {
            const picture = await fetchOutlookProfilePicture(result.accessToken);
            if (picture) {
              sessionStorage.setItem('racs_user_picture', picture);
            }
          }

          onLogin();
          navigate('/dashboard');
        }
      } catch (err) {
        console.error('Outlook redirect login error:', err);
        setError('Outlook login failed. Please try again.');
      }
    };

    // This call was missing before — without it, the function above was
    // defined but never run, so a returning Outlook redirect was never
    // processed.
    processRedirectResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (email === 'racsadmin' && password === 'admin') {
      onLogin();              // update auth state
      navigate('/dashboard'); // ✅ NAVIGATE AFTER LOGIN
    } else {
      setError('Invalid credentials');
    }
  };

  // Whether to show the "Sign in with Google" button — only when a real
  // client ID has been configured in .env.
  const googleLoginEnabled = Boolean(process.env.REACT_APP_GOOGLE_CLIENT_ID);

  const handleGoogleSuccess = (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) {
      setError('Google login failed. Please try again.');
      return;
    }

    const decoded: any = jwtDecode(credentialResponse.credential);
    const googleEmail = (decoded.email || '').toLowerCase();

    if (!isEmailAllowed(googleEmail, ALLOWED_GOOGLE_EMAILS)) {
      setError('This Google account is not authorized to access this application.');
      return; // stop here — no session is created, user stays on the login page
    }

    sessionStorage.setItem('racs_auth_method', 'google');
    sessionStorage.setItem('racs_user_email', decoded.email ?? '');
    sessionStorage.setItem('racs_user_name', decoded.name ?? '');
    sessionStorage.setItem('racs_user_picture', decoded.picture ?? '');

    onLogin();
    navigate('/dashboard');
  };

  const handleGoogleError = () => {
    setError('Google login failed. Please try again.');
  };

  // Handles the "Login with Outlook" flow via full-page redirect. Fully
  // separate from the existing username/password and Google paths above —
  // neither is touched. Redirect is used instead of a popup because
  // loginPopup is prone to MSAL losing track of the popup window handle
  // (a known MSAL.js issue), which left the popup stuck open indefinitely.
  const handleOutlookLogin = async () => {
    if (isOutlookLoginInFlightRef.current) {
      return;
    }
    isOutlookLoginInFlightRef.current = true;

    sessionStorage.removeItem('msal.interaction.status');

    setIsOutlookLoading(true);
    setError('');

    try {
      // This navigates the whole page away to Microsoft's login page.
      // Nothing after this line runs until the redirect completes and this
      // component remounts — the useEffect above then handles the result.
      await msalInstance.loginRedirect(msLoginRequest);
    } catch (err) {
      console.error('Outlook login error:', err);

      if (err instanceof InteractionRequiredAuthError) {
        setError('Outlook login requires additional verification. Please try again.');
      } else {
        setError('Outlook login failed. Please try again.');
      }

      setIsOutlookLoading(false);
      isOutlookLoginInFlightRef.current = false;
    }
  };

  return (
      <div className="login-container">
        <form className="login-form" onSubmit={handleSubmit}>
          <h2>Racs Login</h2>

          {error && <p style={{ color: 'red' }}>{error}</p>}

          <input
              type="text"
              placeholder="Username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
          />

          <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
          />

          <button type="submit">Login</button>

          {googleLoginEnabled && (
              <>
                <div className="login-divider">
                  <span>or</span>
                </div>

                <div className="google-login-wrapper">
                  <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                      useOneTap={false}
                      width="236"
                  />
                </div>
              </>
          )}

          {outlookLoginEnabled && (
              <>
                {!googleLoginEnabled && (
                    <div className="login-divider">
                      <span>or</span>
                    </div>
                )}

                <button
                    type="button"
                    className="outlook-login-button"
                    onClick={handleOutlookLogin}
                    disabled={isOutlookLoading}
                >
                  <svg width="18" height="18" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <rect x="1" y="1" width="10" height="10" fill="#f25022" />
                    <rect x="12" y="1" width="10" height="10" fill="#7fba00" />
                    <rect x="1" y="12" width="10" height="10" fill="#00a4ef" />
                    <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
                  </svg>
                  {isOutlookLoading ? 'Signing in…' : 'Login with Outlook'}
                </button>
              </>
          )}
        </form>
      </div>
  );
};

export default Login;